import { DetailedHTMLProps, TextareaHTMLAttributes, forwardRef, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { InputProps } from './helpers';
import SC from './SC';
import useInput from './useInput';

interface TextAreaContainerProps extends InputProps {
  minHeight?: number;
  maxHeight?: number;
}

const TextAreaContainer = styled.textarea<SC<TextAreaContainerProps>>`
  ${({ sc: { size, minHeight = 80, maxHeight } = {}, theme }) => css`
    resize: none;
    overflow: auto;

    ${minHeight && css`
      min-height: ${minHeight}px;
    `}

    ${maxHeight && css`
      max-height: ${maxHeight}px;
    `}

    ${useInput}
    ${theme.useSizes(size, 'padding', ['5px', '5px', '10px', '10px', '10px'])}
  `}
`;

type TextAreaAttributes = TextareaHTMLAttributes<HTMLTextAreaElement>;
type TextAreaHTMLProps = DetailedHTMLProps<TextAreaAttributes, HTMLTextAreaElement>;

export interface TextAreaProps extends SC<TextAreaContainerProps>, TextAreaHTMLProps { }

const TextArea = forwardRef((props: TextAreaProps, ref) => {
  const textAreaRef = useRef<HTMLTextAreaElement>();

  useEffect(() => {
    if (props.rows) {
      return undefined;
    }

    const textArea = textAreaRef.current;
    const offset = textArea.offsetHeight - textArea.clientHeight;

    textArea.style.height = `${textArea.scrollHeight + offset}px`;

    const inputListener = () => {
      textArea.style.height = 'auto';
      textArea.style.height = `${textArea.scrollHeight + offset}px`;
    };

    textArea.addEventListener('input', inputListener);

    return () => {
      textArea.removeEventListener('input', inputListener);
    };
  }, [props.rows]);

  return (
    <TextAreaContainer
      {...props}
      ref={(element) => {
        textAreaRef.current = element;
        if (ref && typeof ref !== 'function') {
          ref.current = element;
        }
        if (ref && typeof ref === 'function') {
          ref(element);
        }
      }}
    />
  );
});

export default TextArea;
